<script setup>
import { computed, nextTick, ref } from 'vue';
import { Head, useForm } from '@inertiajs/vue3';

import backgroundImage from '@/../image/maillog_background.jpg';

import AuthenticationCard from '@/Components/AuthenticationCard.vue';
import TextField from '@/Components/TextField.vue';

const recovery = ref(false);
const recoveryCodeInput = ref(null);
const codeInput = ref(null);

const form = useForm({
    code: '',
    recovery_code: '',
});

const backgroundStyle = computed(() => ({
    backgroundImage: "url('" + backgroundImage + "')",
    backgroundPosition: 'center',
    backgroundSize: 'cover',
}));

const toggleRecovery = async () => {
    recovery.value = !recovery.value;

    form.clearErrors();
    form.reset(recovery.value ? 'code' : 'recovery_code');

    await nextTick();

    if (recovery.value) {
        recoveryCodeInput.value.focus();
    } else {
        codeInput.value.focus();
    }
};

const submit = () => {
    form.post(route('two-factor.login'), {
        onError: () => {
            if (recovery.value) {
                recoveryCodeInput.value.focus();
            } else {
                codeInput.value.focus();
            }
        },
    });
};
</script>

<script>
export default { layout: null };
</script>

<template>
    <Head :title="$t('Two Factor Authentication')" />

    <div class="tw-h-screen tw-w-screen" :style="backgroundStyle">
        <AuthenticationCard>
            <div class="tw-mb-4 tw-text-sm tw-text-gray-600">
                <template v-if="!recovery">
                    {{
                        $t(
                            'Please confirm access to your account by entering the authentication code provided by your authenticator application.',
                        )
                    }}
                </template>

                <template v-else>
                    {{
                        $t(
                            'Please confirm access to your account by entering one of your emergency recovery codes.',
                        )
                    }}
                </template>
            </div>

            <v-form @submit.prevent="submit">
                <TextField
                    v-if="!recovery"
                    ref="codeInput"
                    v-model="form.code"
                    type="text"
                    inputmode="numeric"
                    autofocus
                    autocomplete="one-time-code"
                    :label="$t('Code')"
                    :error-messages="form.errors.code"
                    required
                />

                <TextField
                    v-else
                    ref="recoveryCodeInput"
                    v-model="form.recovery_code"
                    type="text"
                    autocomplete="one-time-code"
                    :label="$t('Recovery Code')"
                    :error-messages="form.errors.recovery_code"
                    required
                />

                <div class="tw-mt-4 tw-flex tw-items-center tw-justify-end">
                    <v-btn
                        type="button"
                        variant="text"
                        color="primary"
                        @click="toggleRecovery"
                    >
                        <template v-if="!recovery">
                            {{ $t('Use a recovery code') }}
                        </template>

                        <template v-else>
                            {{ $t('Use an authentication code') }}
                        </template>
                    </v-btn>

                    <v-btn
                        type="submit"
                        class="tw-ml-4"
                        color="primary"
                        :loading="form.processing"
                    >
                        {{ $t('Log in') }}
                    </v-btn>
                </div>
            </v-form>
        </AuthenticationCard>
    </div>
</template>
